// Minimal, dependency-free Supabase Auth client.
//
// We deliberately avoid @supabase/supabase-js: all we need is the Auth REST API
// (GoTrue) plus the access token to call our own Pages Functions, which already
// enforce Row-Level Security. Keeping this as plain fetch means no extra build
// dependency and identical behaviour in the browser and at the edge.

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? "").replace(/\/+$/, "");
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";

const SESSION_KEY = "veleo.auth.session";

export interface AuthUser {
  id: string;
  email: string | null;
}

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  /** Unix seconds at which the access token expires. */
  expiresAt: number;
  user: AuthUser;
}

export type AuthEvent = "SIGNED_IN" | "SIGNED_OUT" | "TOKEN_REFRESHED";

type Listener = (session: AuthSession | null, event: AuthEvent) => void;

const listeners = new Set<Listener>();

export function isAuthConfigured(): boolean {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
}

// ---------------------------------------------------------------------------
// Session storage
// ---------------------------------------------------------------------------

function readSession(): AuthSession | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as AuthSession;
    if (!parsed?.accessToken || !parsed?.refreshToken) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(session: AuthSession | null, event: AuthEvent) {
  if (typeof window !== "undefined") {
    if (session) {
      window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } else {
      window.localStorage.removeItem(SESSION_KEY);
    }
  }

  for (const listener of listeners) {
    listener(session, event);
  }
}

let currentSession: AuthSession | null = readSession();

export function getSession(): AuthSession | null {
  return currentSession;
}

export function onAuthChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ---------------------------------------------------------------------------
// Low-level REST helpers
// ---------------------------------------------------------------------------

interface GoTrueSession {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  expires_at?: number;
  user?: { id: string; email?: string | null };
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  return {
    apikey: SUPABASE_ANON_KEY,
    "Content-Type": "application/json",
    ...extra,
  };
}

function toSession(payload: GoTrueSession): AuthSession {
  const expiresAt =
    payload.expires_at ??
    Math.floor(Date.now() / 1000) + (payload.expires_in ?? 3600);

  return {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt,
    user: {
      id: payload.user?.id ?? "",
      email: payload.user?.email ?? null,
    },
  };
}

async function readError(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as {
      error_description?: string;
      msg?: string;
      message?: string;
      error?: string;
    };
    return (
      data.error_description ||
      data.msg ||
      data.message ||
      data.error ||
      `auth_request_failed_${response.status}`
    );
  } catch {
    return `auth_request_failed_${response.status}`;
  }
}

// ---------------------------------------------------------------------------
// Public auth actions
// ---------------------------------------------------------------------------

export type AuthResult =
  | { ok: true; session: AuthSession | null; needsConfirmation: boolean }
  | { ok: false; error: string };

function ensureConfigured(): { ok: false; error: string } | null {
  if (!isAuthConfigured()) {
    return { ok: false, error: "auth_not_configured" };
  }
  return null;
}

export async function signUpWithPassword(email: string, password: string): Promise<AuthResult> {
  const guard = ensureConfigured();
  if (guard) return guard;

  const redirectTo =
    typeof window !== "undefined" ? `${window.location.origin}/#/login` : undefined;

  const response = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      email,
      password,
      ...(redirectTo ? { options: { email_redirect_to: redirectTo } } : {}),
    }),
  });

  if (!response.ok) {
    return { ok: false, error: await readError(response) };
  }

  const payload = (await response.json()) as GoTrueSession;

  // When email confirmation is enabled, no tokens are returned yet.
  if (!payload.access_token) {
    return { ok: true, session: null, needsConfirmation: true };
  }

  const session = toSession(payload);
  currentSession = session;
  writeSession(session, "SIGNED_IN");
  return { ok: true, session, needsConfirmation: false };
}

export async function signInWithPassword(email: string, password: string): Promise<AuthResult> {
  const guard = ensureConfigured();
  if (guard) return guard;

  const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    return { ok: false, error: await readError(response) };
  }

  const session = toSession((await response.json()) as GoTrueSession);
  currentSession = session;
  writeSession(session, "SIGNED_IN");
  return { ok: true, session, needsConfirmation: false };
}

export async function signInWithMagicLink(email: string): Promise<AuthResult> {
  const guard = ensureConfigured();
  if (guard) return guard;

  const redirectTo =
    typeof window !== "undefined" ? `${window.location.origin}/#/login` : undefined;

  const response = await fetch(`${SUPABASE_URL}/auth/v1/otp`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      email,
      create_user: true,
      ...(redirectTo ? { options: { email_redirect_to: redirectTo } } : {}),
    }),
  });

  if (!response.ok) {
    return { ok: false, error: await readError(response) };
  }

  // The session arrives later, when the user clicks the emailed link.
  return { ok: true, session: null, needsConfirmation: true };
}

export async function signOut(): Promise<void> {
  const session = currentSession;
  currentSession = null;
  writeSession(null, "SIGNED_OUT");

  if (session && SUPABASE_URL) {
    try {
      await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
        method: "POST",
        headers: authHeaders({ Authorization: `Bearer ${session.accessToken}` }),
      });
    } catch {
      // Local sign-out already happened; ignore network failures.
    }
  }
}

async function refreshSession(refreshToken: string): Promise<AuthSession | null> {
  const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    // Refresh token is invalid/expired — force a clean sign-out.
    currentSession = null;
    writeSession(null, "SIGNED_OUT");
    return null;
  }

  const session = toSession((await response.json()) as GoTrueSession);
  currentSession = session;
  writeSession(session, "TOKEN_REFRESHED");
  return session;
}

/**
 * Returns a valid access token, refreshing it first if it is missing or about
 * to expire. Returns null when the user is not (or no longer) signed in.
 */
export async function getAccessToken(): Promise<string | null> {
  const session = currentSession;
  if (!session) {
    return null;
  }

  const now = Math.floor(Date.now() / 1000);
  if (session.expiresAt - now > 60) {
    return session.accessToken;
  }

  const refreshed = await refreshSession(session.refreshToken);
  return refreshed?.accessToken ?? null;
}

/**
 * Detects a magic-link / email-confirmation redirect. Supabase returns the new
 * session in the URL fragment (#access_token=...&refresh_token=...). If present,
 * store it and clean the URL. Returns the session it consumed, if any.
 */
export function consumeUrlSession(): AuthSession | null {
  if (typeof window === "undefined") {
    return null;
  }

  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;

  if (!hash.includes("access_token=")) {
    return null;
  }

  const params = new URLSearchParams(hash);
  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");

  if (!accessToken || !refreshToken) {
    return null;
  }

  const expiresIn = Number(params.get("expires_in") || "3600");
  const session: AuthSession = {
    accessToken,
    refreshToken,
    expiresAt: Math.floor(Date.now() / 1000) + (Number.isFinite(expiresIn) ? expiresIn : 3600),
    user: { id: "", email: null },
  };

  currentSession = session;
  writeSession(session, "SIGNED_IN");

  // Strip auth params from the URL but keep the user on the login route so the
  // app can finish onboarding/bootstrap.
  window.history.replaceState(null, "", "#/login");
  return session;
}

// ---------------------------------------------------------------------------
// Account bootstrap RPC (creates org + membership for a new user)
// ---------------------------------------------------------------------------

export interface BootstrapAccountInput {
  orgName: string;
  orgType: "restaurant" | "supplier";
  fullName?: string;
  city?: string;
  country?: string;
  email?: string;
}

export type BootstrapAccountResult =
  | { ok: true; organizationId: string; type: string; role: string; created: boolean }
  | { ok: false; error: string };

export async function bootstrapAccount(
  input: BootstrapAccountInput,
): Promise<BootstrapAccountResult> {
  const token = await getAccessToken();
  if (!token) {
    return { ok: false, error: "not_authenticated" };
  }

  const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/bootstrap_account`, {
    method: "POST",
    headers: authHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify({
      p_org_name: input.orgName,
      p_org_type: input.orgType,
      p_full_name: input.fullName ?? null,
      p_city: input.city ?? null,
      p_country: input.country ?? "BA",
      p_email: input.email ?? null,
    }),
  });

  if (!response.ok) {
    return { ok: false, error: await readError(response) };
  }

  const payload = (await response.json()) as
    | {
        organization_id: string;
        type: string;
        role: string;
        created: boolean;
      }
    | Array<{
        organization_id: string;
        type: string;
        role: string;
        created: boolean;
      }>;
  const data = Array.isArray(payload) ? payload[0] : payload;

  if (!data?.organization_id) {
    return { ok: false, error: "account_not_created" };
  }

  return {
    ok: true,
    organizationId: data.organization_id,
    type: data.type,
    role: data.role,
    created: data.created,
  };
}
